import { Avatar, Col, Tooltip } from "antd-v5";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import { DateFormatStrings } from "hooks/useDateFormats";
import { find, isFunction, isNull, map } from "lodash";
import moment from "moment";
import React, { ReactNode, useState } from "react";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import { CommentResponse } from "types/comment";
import ModalUtil from "utils/modalUtil";
import { purifyHtml } from "utils/parserUtil";
import Sorters from "utils/sorters";
import { CellRenderers } from "utils/tableUtils";

import ACLink from "ac-components/ACLink/ACLink";
import ACCommentEditor, {
  ACCommentEditorProps,
  OnSumbitProps,
  getAdditionalExtions,
} from "ac-components/ac-comment-editor/ACCommentEditor";
import ACEditor from "ac-components/ac-editor/ACEditor";

import WarningPopup from "./WarningPopup";

const ACComment = ({
  commentList,
  showEditButton = true,
  onCommentSubmit,
  users,
  renderExtraMeta,
}: {
  commentList: CommentResponse[] | undefined;
  showEditButton?: boolean | ((item: CommentResponse) => boolean);
  onCommentSubmit?: (
    commentId: number,
    payload: OnSumbitProps,
  ) => Promise<void>;
  users: UserInfoProjectorImpl[];
  renderExtraMeta?: (item: CommentResponse) => ReactNode | null;
}) => {
  const activeUser = useActiveUserContext();
  const [editID, setEditID] = useState<number | null>();
  const [hasChange, setHasChange] = useState<boolean>(false);

  const onEditComment = (id: number | null) => {
    if (hasChange && editID) {
      ModalUtil.show({
        content: (
          <WarningPopup
            //@ts-ignore
            title={`The changes in the comment will not be saved. Are you sure want to continue?`}
            okText="Yes"
            onOk={() => {
              setEditID(id);
              setHasChange(false);
            }}
          />
        ),
      });
    } else {
      setEditID(id);
      setHasChange(false);
    }
  };

  const onCancel = () => {
    onEditComment(null);
  };

  const onChange = (content: string) => {
    const obj = find(commentList, (l) => l.id === editID);
    if (obj) {
      setHasChange(obj.comment !== content);
    }
  };

  const onSubmit: ACCommentEditorProps["onSubmit"] = async (payload) => {
    if (onCommentSubmit && editID) {
      await onCommentSubmit(editID, payload);
      if (showEditButton) {
        setEditID(null);
        setHasChange(false);
      }
    }
  };

  const renderComment = (comment: CommentResponse) => {
    const {
      createdBy,
      comment: note,
      createdAt,
      updatedAt,
      id,
      isEdited,
    } = comment;
    const userName: string = isNull(comment.createdBy)
      ? "SYSTEM"
      : find(users, { email: createdBy })?.displayName || createdBy;
    const content = purifyHtml(note);

    const matches = userName.match(/\b(\w)/g);
    const dateTime = isEdited ? updatedAt : createdAt;

    let _showEditButton;
    if (isFunction(showEditButton)) {
      _showEditButton = showEditButton(comment);
    } else {
      _showEditButton = showEditButton;
    }

    return (
      <>
        <div className="d-flex align-items-center">
          <Avatar size={36} style={{ fontSize: 36 }}>
            {matches?.join("") || userName[0]}
          </Avatar>
          <Tooltip title={createdBy}>
            <span style={{ marginLeft: 10, fontSize: 14, fontWeight: 600 }}>
              {userName}
            </span>
          </Tooltip>
          {renderExtraMeta?.(comment)}
          <div style={{ marginLeft: "auto", fontSize: 12 }}>
            {isEdited && (
              <Tooltip
                title={
                  <div>
                    Created On: {CellRenderers.TIME(createdAt)}
                    {isEdited ? (
                      <div>Edited On: {CellRenderers.TIME(updatedAt)}</div>
                    ) : (
                      ""
                    )}
                  </div>
                }
                placement="top"
              >
                <span className="text-muted">Edited</span>
              </Tooltip>
            )}{" "}
            <Tooltip title={CellRenderers.TIME(dateTime)}>
              <span style={{ color: "#70737B" }}>
                {moment(dateTime).format(DateFormatStrings.DATE)}
              </span>
            </Tooltip>
          </div>
        </div>
        <div className="m-t-xs">
          {id && editID && id === editID ? (
            <ACCommentEditor
              onSubmit={onSubmit}
              initialValue={note}
              defaultFocus
              onCancel={onCancel}
              onChange={onChange}
              submitText="Save"
              users={users}
            />
          ) : (
            <>
              <ACEditor
                value={content}
                extensions={getAdditionalExtions({ users })}
                disabled
              />
              {activeUser?.email === createdBy && _showEditButton && (
                <div className="m-t-xs">
                  <ACLink primary={false} onClick={() => onEditComment(id)}>
                    Edit
                  </ACLink>
                </div>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {map(
        (commentList || []).sort(Sorters.DATE("createdAt", true)),
        (comment: CommentResponse, i: number) => {
          return (
            <Col
              key={i}
              className="border-bottom-light"
              style={{ padding: "16px 0" }}
            >
              {renderComment(comment)}
            </Col>
          );
        },
      )}
    </>
  );
};

export default ACComment;
